import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Youtube } from "lucide-react";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/api";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const res = await subscribeNewsletter(email.trim(), "footer");
      if (res?.ok) {
        toast(res.already ? "You're already on the list." : "Welcome.");
        setEmail("");
      }
    } catch {
      toast("We couldn't add you. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="dark-section relative pt-32 md:pt-48 pb-16 md:pb-20"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-20">
          {/* Newsletter */}
          <div className="md:col-span-6">
            <p className="hairline opacity-60 mb-5">The Newsletter</p>
            <h3 className="font-serif italic text-3xl md:text-4xl leading-[1.1] max-w-md">
              You will know first when a Chapter opens.
            </h3>
            <form onSubmit={onSubmit} className="mt-10 max-w-md">
              <div className="flex items-end gap-6">
                <div className="flex-1">
                  <label className="hairline opacity-50 block mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="invisible-input"
                    data-testid="newsletter-email-input"
                    autoComplete="email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="link-quiet font-serif italic text-lg disabled:opacity-40 pb-1"
                  data-testid="newsletter-submit"
                >
                  {submitting ? "…" : "Subscribe"}
                </button>
              </div>
              <p className="hairline opacity-40 mt-4 leading-relaxed">
                One letter per chapter. Never sold, never shared.
              </p>
            </form>
          </div>

          {/* Nav */}
          <div className="md:col-span-3">
            <p className="hairline opacity-60 mb-5">The Estate</p>
            <ul className="space-y-3 font-serif italic text-lg">
              <li><Link to="/collector-editions" data-testid="footer-the190">Collector Editions</Link></li>
              <li><Link to="/the-artist" data-testid="footer-artist">The Artist</Link></li>
              <li><Link to="/work" data-testid="footer-work">Work</Link></li>
              <li><Link to="/exhibitions" data-testid="footer-exhibitions">Exhibitions</Link></li>
              <li><Link to="/chapter-ii" data-testid="footer-chapter-ii">Chapter II</Link></li>
              <li><Link to="/press" data-testid="footer-press">Press</Link></li>
              <li><Link to="/reviews" data-testid="footer-reviews">Reviews</Link></li>
              <li><Link to="/contact" data-testid="footer-contact">Contact</Link></li>
              <li><Link to="/collector" className="opacity-60" data-testid="footer-collector">Collectors</Link></li>
            </ul>
          </div>

          {/* Social + support */}
          <div className="md:col-span-3">
            <p className="hairline opacity-60 mb-5">Elsewhere</p>
            <ul className="space-y-3 font-serif italic text-lg">
              <li>
                <a
                  href="https://www.instagram.com/nickfraserart"
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-3"
                  data-testid="footer-instagram"
                >
                  <Instagram size={16} className="opacity-60" /> @nickfraserart
                </a>
              </li>
              <li>
                <a
                  href="https://www.youtube.com/@Nickfraserart"
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-3"
                  data-testid="footer-youtube"
                >
                  <Youtube size={16} className="opacity-60" /> @Nickfraserart
                </a>
              </li>
              <li>
                <a
                  href="https://www.youtube.com/watch?v=bjPlwtp4zzg"
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-3"
                  data-testid="footer-film"
                >
                  <span className="opacity-60 font-mono text-xs">▷</span> The short film
                </a>
              </li>
            </ul>

            <p className="hairline opacity-50 mt-10 mb-3">Support the estate</p>
            <Link
              to="/contact?topic=support"
              className="link-quiet font-serif italic text-lg"
              data-testid="footer-support"
            >
              Make a contribution
            </Link>
          </div>
        </div>

        <div className="rule mt-24 mb-8 opacity-30" />

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[color:var(--bone)]">
          <p className="hairline opacity-50">
            Nick Fraser · 1946 — 2023 · The East End Van Gogh
          </p>
          <p className="hairline opacity-40">
            Curated by Marcus O'Shaughnessy · Custodianship by Mark O'Shaughnessy
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
